/**
 * All UserEvaluationQuestion CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.userEvaluationQuestion;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_USER_EVALUATION_QUESTION = "INVALIDATE_SELECTED_USER_EVALUATION_QUESTION"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_USER_EVALUATION_QUESTION
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleUserEvaluationQuestionById(id))
  } else {
    return dispatch(returnSingleUserEvaluationQuestionPromise(id)); // return promise that contains userEvaluationQuestion
  }
}

export const returnSingleUserEvaluationQuestionPromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_SINGLE_USER_EVALUATION_QUESTION_WITHOUT_FETCHING"
      , id: id
      , item: getState().userEvaluationQuestion.byId[id]
      , success: true
    })
  });
}

export const REQUEST_SINGLE_USER_EVALUATION_QUESTION = "REQUEST_SINGLE_USER_EVALUATION_QUESTION";
function requestSingleUserEvaluationQuestion(id) {
  return {
    type: REQUEST_SINGLE_USER_EVALUATION_QUESTION
    , id
  }
}

export const RECEIVE_SINGLE_USER_EVALUATION_QUESTION = "RECEIVE_SINGLE_USER_EVALUATION_QUESTION";
function receiveSingleUserEvaluationQuestion(json) {
  return {
    type: RECEIVE_SINGLE_USER_EVALUATION_QUESTION
    , id: json.userEvaluationQuestion ? json.userEvaluationQuestion._id : null
    , item: json.userEvaluationQuestion
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function fetchSingleUserEvaluationQuestionById(userEvaluationQuestionId) {
  return dispatch => {
    dispatch(requestSingleUserEvaluationQuestion(userEvaluationQuestionId))
    return callAPI(`/api/user-evaluation-questions/${userEvaluationQuestionId}`)
      .then(json => dispatch(receiveSingleUserEvaluationQuestion(json)))
  }
}

export const ADD_SINGLE_USER_EVALUATION_QUESTION_TO_MAP = "ADD_SINGLE_USER_EVALUATION_QUESTION_TO_MAP";
export function addSingleUserEvaluationQuestionToMap(item) {
  return {
    type: ADD_SINGLE_USER_EVALUATION_QUESTION_TO_MAP
    , item
  }
}

export const REQUEST_SINGLE_USER_EVALUATION_QUESTION_BY_MODULE = "REQUEST_SINGLE_USER_EVALUATION_QUESTION_BY_MODULE";
function requestSingleUserEvaluationQuestionByModule(contentModuleId) {
  return {
    type: REQUEST_SINGLE_USER_EVALUATION_QUESTION_BY_MODULE
    , contentModuleId
  }
}

export function fetchSingleUserEvaluationQuestionByModule(contentModuleId) {
  return dispatch => {
    dispatch(requestSingleUserEvaluationQuestionByModule(contentModuleId))
    return callAPI(`/api/user-evaluation-questions/current/${contentModuleId}`)
      .then(json => dispatch(receiveSingleUserEvaluationQuestion(json)))
  }
}

export const REQUEST_CREATE_USER_EVALUATION_QUESTION = "REQUEST_CREATE_USER_EVALUATION_QUESTION";
function requestCreateUserEvaluationQuestion(userEvaluationQuestion) {
  return {
    type: REQUEST_CREATE_USER_EVALUATION_QUESTION
    , userEvaluationQuestion
  }
}

export const RECEIVE_CREATE_USER_EVALUATION_QUESTION = "RECEIVE_CREATE_USER_EVALUATION_QUESTION";
function receiveCreateUserEvaluationQuestion(json) {
  return {
    type: RECEIVE_CREATE_USER_EVALUATION_QUESTION
    , id: json.userEvaluationQuestion ? json.userEvaluationQuestion._id : null
    , item: json.userEvaluationQuestion
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCreateUserEvaluationQuestion(data) {
  return dispatch => {
    dispatch(requestCreateUserEvaluationQuestion(data))
    return callAPI('/api/user-evaluation-questions', 'POST', data)
      .then(json => dispatch(receiveCreateUserEvaluationQuestion(json)))
  }
}

export const REQUEST_UPDATE_USER_EVALUATION_QUESTION = "REQUEST_UPDATE_USER_EVALUATION_QUESTION";
function requestUpdateUserEvaluationQuestion(userEvaluationQuestion) {
  return {
    id: userEvaluationQuestion ? userEvaluationQuestion._id : null
    , userEvaluationQuestion
    , type: REQUEST_UPDATE_USER_EVALUATION_QUESTION
  }
}

export const RECEIVE_UPDATE_USER_EVALUATION_QUESTION = "RECEIVE_UPDATE_USER_EVALUATION_QUESTION";
function receiveUpdateUserEvaluationQuestion(json) {
  return {
    type: RECEIVE_UPDATE_USER_EVALUATION_QUESTION
    , id: json.userEvaluationQuestion ? json.userEvaluationQuestion._id : null
    , item: json.userEvaluationQuestion
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendUpdateUserEvaluationQuestion(data) {
  return dispatch => {
    dispatch(requestUpdateUserEvaluationQuestion(data))
    return callAPI(`/api/user-evaluation-questions/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateUserEvaluationQuestion(json)))
  }
}

export const REQUEST_DELETE_USER_EVALUATION_QUESTION = "REQUEST_DELETE_USER_EVALUATION_QUESTION";
function requestDeleteUserEvaluationQuestion(id) {
  return {
    type: REQUEST_DELETE_USER_EVALUATION_QUESTION
    , id
  }
}

export const RECEIVE_DELETE_USER_EVALUATION_QUESTION = "RECEIVE_DELETE_USER_EVALUATION_QUESTION";
function receiveDeleteUserEvaluationQuestion(id, json) {
  return {
    id
    , error: json.message
    , receivedAt: Date.now()
    , success: json.success
    , type: RECEIVE_DELETE_USER_EVALUATION_QUESTION
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteUserEvaluationQuestion(id))
    return callAPI(`/api/user-evaluation-questions/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteUserEvaluationQuestion(id, json)))
  }
}


/**
 * USER_EVALUATION_QUESTION LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest userEvaluationQuestionLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.userEvaluationQuestion.lists, {});
  let list = { ...state.userEvaluationQuestion.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnUserEvaluationQuestionListPromise(...listArgs));
  }
}

export const returnUserEvaluationQuestionListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.userEvaluationQuestion.byId[id]);

  return new Promise((resolve) => {
    resolve({
      list: listItems
      , listArgs: listArgs
      , success: true
      , type: "RETURN_USER_EVALUATION_QUESTION_LIST_WITHOUT_FETCHING"
    })
  });
}

export const REQUEST_USER_EVALUATION_QUESTION_LIST = "REQUEST_USER_EVALUATION_QUESTION_LIST"
function requestUserEvaluationQuestionList(listArgs) {
  return {
    type: REQUEST_USER_EVALUATION_QUESTION_LIST
    , listArgs
  }
}

export const RECEIVE_USER_EVALUATION_QUESTION_LIST = "RECEIVE_USER_EVALUATION_QUESTION_LIST"
function receiveUserEvaluationQuestionList(json, listArgs) {
  return {
    type: RECEIVE_USER_EVALUATION_QUESTION_LIST
    , listArgs
    , list: json.userEvaluationQuestions
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export const ADD_USER_EVALUATION_QUESTION_TO_LIST = "ADD_USER_EVALUATION_QUESTION_TO_LIST";
export function addUserEvaluationQuestionToList(id, ...listArgs) {
  // console.log("Add userEvaluationQuestion to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_USER_EVALUATION_QUESTION_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_USER_EVALUATION_QUESTION_FROM_LIST = "REMOVE_USER_EVALUATION_QUESTION_FROM_LIST"
export function removeUserEvaluationQuestionFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_USER_EVALUATION_QUESTION_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    dispatch(requestUserEvaluationQuestionList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/user-evaluation-questions/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/user-evaluation-questions/by-[ARG1]/[ARG2]".
     *    ex: /api/user-evaluation-questions/by-category/:category
     *  if 2nd arg is an array, though, return "/api/user-evaluation-questions/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */

    let apiTarget = "/api/user-evaluation-questions";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of userEvaluationQuestions given an array of userEvaluationQuestion id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveUserEvaluationQuestionList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_USER_EVALUATION_QUESTION_FILTER = "SET_USER_EVALUATION_QUESTION_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_USER_EVALUATION_QUESTION_FILTER
    , filter
    , listArgs
  }
}

export const SET_USER_EVALUATION_QUESTION_PAGINATION = "SET_USER_EVALUATION_QUESTION_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_USER_EVALUATION_QUESTION_PAGINATION
    , pagination
    , listArgs
  }
}

export const INVALIDATE_USER_EVALUATION_QUESTION_LIST = "INVALIDATE_USER_EVALUATION_QUESTION_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: INVALIDATE_USER_EVALUATION_QUESTION_LIST
    , listArgs
  }
}



// WEBPACK FOOTER //
// modules/userEvaluationQuestion/userEvaluationQuestionActions.js